import { ReactNode, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  FileText,
  FileCheck,
  User,
  ArrowLeftFromLine,
  ArrowRightFromLine,
  Shield,
  Image as ImageIcon,
  Store,
  ChartNoAxesCombined,
  Loader2,
} from 'lucide-react'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useNotificationSSE } from '@/hooks/useNotificationSSE'
import { useTokenRefresh } from '@/hooks/useTokenRefresh'
import { useVersionCheck } from '@/hooks/useVersionCheck'
import { useSSOLogout } from '@/hooks/useLogin'
import { useMyDashboardAccess } from '@/hooks/useReportPermissions'
import { NotificationBell } from '@/components/Notifications/NotificationBell'
import { useSidebarStore } from '@/stores/useSidebarStore'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import {
  Sidebar,
  SidebarContent,
  SidebarNav,
  type SidebarNavItem,
} from '@/components/ui/sidebar-nav'
import { Separator } from '@/components/ui/separator'
import { Toaster } from '@/components/common/Toaster'
import { WAVE_PLANNING_ENABLED } from '@/components/WavePlanning/feature'

interface LayoutProps {
  children: ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { mutate: logout, isPending: isLoggingOut } = useSSOLogout()
  const { isCollapsed, isAutoCollapsed, toggle, setAutoCollapsed } =
    useSidebarStore()
  const { isAdmin, isManager, name } = useCurrentUser()
  const { data: dashboardAccess } = useMyDashboardAccess()
  const hasIfpAccess =
    !!dashboardAccess?.is_admin ||
    (dashboardAccess?.ifp_report_ids.length ?? 0) > 0
  const isDesktop = useMediaQuery('(min-width: 1280px)')
  useNotificationSSE()
  useTokenRefresh()
  useVersionCheck()

  useEffect(() => {
    setAutoCollapsed(!isDesktop)
  }, [isDesktop, setAutoCollapsed])

  const effectiveCollapsed = isCollapsed || isAutoCollapsed

  const navItems: SidebarNavItem[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <LayoutDashboard className="h-4 w-4" />,
      children: [
        { id: 'dashboard-cat', label: 'CAT', href: '/dashboard/cat' },
        ...(import.meta.env.VITE_ENABLE_IFP === 'true' && hasIfpAccess
          ? [
              {
                id: 'dashboard-ifp',
                label: 'Flow Tool',
                href: '/dashboard/flow-tool',
              },
            ]
          : []),
      ],
    },
    {
      id: 'assortment-plans',
      label: 'Assortment Plans',
      icon: <FileText className="h-4 w-4" />,
      href: '/',
    },
    ...(WAVE_PLANNING_ENABLED
      ? [
          {
            id: 'wave-planning',
            label: 'Wave Planning',
            icon: <ChartNoAxesCombined className="h-4 w-4" />,
            href: '/wave-planning',
          },
        ]
      : []),
    ...(isManager
      ? [
          {
            id: 'approvals',
            label: 'Approvals',
            icon: <FileCheck className="h-4 w-4" />,
            href: '/approvals',
          },
        ]
      : []),
    {
      id: 'images',
      label: 'Images',
      icon: <ImageIcon className="h-4 w-4" />,
      href: '/images',
    },
    {
      id: 'store-models',
      label: 'Store Models',
      icon: <Store className="h-4 w-4" />,
      href: '/store-models',
    },
    ...(isAdmin
      ? [
          {
            id: 'admin',
            label: 'Admin',
            icon: <Shield className="h-4 w-4" />,
            href: '/admin',
          },
        ]
      : []),
  ]

  const getActiveId = () => {
    if (location.pathname.startsWith('/wave-planning')) return 'wave-planning'
    if (location.pathname === '/dashboard/flow-tool') return 'dashboard-ifp'
    if (location.pathname.startsWith('/dashboard')) return 'dashboard-cat'
    if (location.pathname.startsWith('/approvals')) return 'approvals'
    if (location.pathname.startsWith('/images')) return 'images'
    if (location.pathname.startsWith('/store-models')) return 'store-models'
    if (location.pathname.startsWith('/admin')) return 'admin'
    if (location.pathname === '/' || location.pathname.startsWith('/plans/'))
      return 'assortment-plans'
    return undefined
  }

  const handleLogout = () => {
    logout()
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background">
      {/* Full-width Top Nav */}
      <header className="flex h-14 items-center justify-between border-b border-border bg-card px-6">
        {/* Left side - Logo and branding */}
        <div className="flex items-center gap-6">
          <img src="/logo.svg" alt="Michaels" className="h-9 w-auto" />
          <Separator orientation="vertical" className="h-6" />
          <span className="text-base font-medium text-muted-foreground">
            Category Assortment Tool
          </span>
        </div>

        {/* Right side - Help and user info */}
        <div className="flex items-center gap-4">
          <NotificationBell />
          <Separator orientation="vertical" className="h-6" />
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
              <User className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-sm">
              <p className="font-medium text-foreground">{name ?? 'User'}</p>
              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="text-xs text-muted-foreground hover:text-primary cursor-pointer disabled:opacity-50"
              >
                {isLoggingOut ? (
                  <span className="flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Logging out...
                  </span>
                ) : (
                  'Logout'
                )}
              </button>
            </div>
          </div>
        </div>
      </header>
      {/* Sidebar + Content row */}
      <div className="flex flex-1 overflow-hidden">
        <Sidebar isCollapsed={effectiveCollapsed}>
          {/* Collapse/Expand toggle button */}
          <div
            className={`flex p-2 ${effectiveCollapsed ? 'justify-center' : 'justify-end'}`}
          >
            <button
              onClick={toggle}
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
              title={effectiveCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {effectiveCollapsed ? (
                <ArrowRightFromLine className="h-4 w-4" />
              ) : (
                <ArrowLeftFromLine className="h-4 w-4" />
              )}
            </button>
          </div>
          <SidebarContent>
            <SidebarNav
              items={navItems}
              activeId={getActiveId()}
              onItemClick={item => item.href && navigate(item.href)}
              isCollapsed={effectiveCollapsed}
            />
          </SidebarContent>
        </Sidebar>

        <main className="flex-1 overflow-auto bg-background">{children}</main>
      </div>
      <Toaster />
    </div>
  )
}
